import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Compor } from "@/components/jogos/Compor";
import { useOutroNome } from "@/components/jogos/useOutroNome";
import { Adivinhar } from "@/components/jogos/Adivinhar";
import type { VisibleWordWithGuesses as PalavraComPalpites } from "@historia/shared/contracts";

type Tab = "minhas" | "adivinhar";

export function RondaAtiva({
  rondaId,
  email,
  palavras,
  aoAbrir,
}: {
  rondaId: string;
  email: string;
  palavras: PalavraComPalpites[];
  /** Abrir uma palavra é sair desta página para o ecrã de adivinhar. */
  aoAbrir: (id: string) => void;
}) {
  const [tab, setTab] = useState<Tab>("minhas");
  const outro = useOutroNome();
  const reduzir = useReducedMotion();

  const minhas = palavras.filter((p) => p.autor_email === email);
  const dela = palavras.filter((p) => p.autor_email !== email);

  const minhasFeitas = minhas.length;
  const porAdivinhar = dela.filter((p) => p.acertou_na === null && !p.revelada).length;

  return (
    <section>
      {/* Card "Ronda ativa" */}
      <div className="mb-5 rounded-2xl border border-ouro/30 bg-gradient-to-br from-creme-50 to-creme-100 p-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-500" aria-hidden />
          <span className="font-semibold text-tinta-800">Ronda ativa</span>
        </div>
        <p className="mt-1 text-sm text-tinta-600">
          {minhasFeitas > 0 ? "A tua palavra está posta" : "Ainda não puseste a tua palavra"} ·{" "}
          {porAdivinhar > 0 ? "há uma à tua espera" : "nada por adivinhar (por agora)"}
        </p>
      </div>

      {/* Tabs */}
      <div
        role="tablist"
        aria-label="Ronda"
        className="mb-5 grid grid-cols-2 gap-2 rounded-full bg-creme-100 p-1"
      >
        <TabBtn ativo={tab === "minhas"} aoClicar={() => setTab("minhas")}>
          A minha palavra
        </TabBtn>
        <TabBtn ativo={tab === "adivinhar"} aoClicar={() => setTab("adivinhar")}>
          A palavra de {outro}
          {porAdivinhar > 0 && (
            <span className="ml-1.5 rounded-full bg-ouro/20 px-1.5 text-xs text-ouro-600">
              {porAdivinhar}
            </span>
          )}
        </TabBtn>
      </div>

      {/* Conteúdo da tab (com transição suave) */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={reduzir ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduzir ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          {tab === "minhas" ? (
            <Compor rondaId={rondaId} minhas={minhas} />
          ) : (
            <Adivinhar palavras={dela} aoAbrir={aoAbrir} />
          )}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}

function TabBtn({
  ativo,
  aoClicar,
  children,
}: {
  ativo: boolean;
  aoClicar: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={ativo}
      onClick={aoClicar}
      className={`flex items-center justify-center rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
        ativo
          ? "bg-creme text-ouro-600 shadow-sm"
          : "text-tinta-600 hover:text-ouro-600"
      }`}
    >
      {children}
    </button>
  );
}
